<template>
  <div>
    <form-section :title="$t('Imports_Title_Description')">
      <rest-form-item
        :label="$t('Imports_Field_Name')"
        :hint="$t('Imports_Field_Name_Description')"
        prop="name"
      >
        <el-input
          v-model="model.name"
          :placeholder="$t('Imports_Field_Name')"
        />
      </rest-form-item>
    </form-section>

    <form-section
      :title="$t('Imports_Title_DataType')"
      :description="$t('Imports_Title_DataType_Description')"
    >
      <el-row>
        <el-col :span="16">
          <rest-form-item prop="type" :label="$t('Automations_Field_Identity')">
            <radio-group v-model="model.type" :inline="true">
              <radio
                label="profiles"
                :title="$t('Audiences_Type_Users')"
                :border="true"
              ></radio>
              <radio-custom :border="true" label="events">
                <div class="radio-input">
                  <el-row>
                    <el-col :span="8">
                      <div class="custom-event">
                        {{ $t('Imports_Field_Radio_Events') }}
                      </div>
                    </el-col>
                    <el-col :span="16">
                      <el-select
                        :disabled="model.type !== 'events'"
                        v-model="model.config.event"
                        :placeholder="
                          $t('Campaign_EventSetup_SelectEvent_Placeholder')
                        "
                        :remote-method="fetchEventsOnSearch"
                        clearable
                        filterable
                        remote
                      >
                        <el-option
                          v-for="(item, index) in events"
                          :key="index"
                          :label="item.name"
                          :value="item.name"
                        >
                          <el-tooltip :content="item.name" :open-delay="1000">
                            <span class="label">{{ item.name }}</span>
                          </el-tooltip>
                        </el-option>
                      </el-select>
                    </el-col>
                  </el-row>
                </div>
              </radio-custom>
            </radio-group>
            <el-row>
              <el-col :span="12">&nbsp;</el-col>
              <el-col :span="12">
                <rest-form-item prop="config.event" />
              </el-col>
            </el-row>
          </rest-form-item>
        </el-col>
      </el-row>
    </form-section>

    <form-section
      :title="$t('Imports_Title_DataSource')"
      :description="$t('Imports_Title_DataSource_Description')"
      v-loading="loading"
    >
      <template slot="controls" v-if="model.source.type === 'fs'">
        <rest-form-item
          v-show="model.config.columns < 1"
          prop="config.columns"
        />
        <el-button
          type="primary"
          plain
          @click="getSchema"
          :disabled="disableButton"
          icon="el-icon-refresh-right"
        >
          {{ $t('Imports_Button_RefreshSchema') }}
        </el-button>
      </template>

      <el-row>
        <el-col :span="6">
          <rest-form-item :label="$t('Imports_Field_Type')" prop="source.type">
            <enum-select v-model="model.source.type" :options="sourceEnum" />
          </rest-form-item>
        </el-col>

        <el-col :span="6" v-if="model.source.type === 'upload'">
          <rest-form-item
            :label="$t('Imports_Field_Type_Upload')"
            prop="config.columns"
          >
            <csv-upload
              :loading.sync="loading"
              :columns.sync="model.config.columns"
              @init="init"
              :file-id.sync="model.source.file_id"
            />
          </rest-form-item>
        </el-col>

        <el-col :span="6" v-else-if="model.source.type === 'fs'">
          <rest-form-item
            :label="$t('Imports_Field_FileStorage')"
            prop="source.integration_id"
          >
            <el-select v-model="model.source.integration_id" value-key="name">
              <el-option
                v-for="item in steps[currentStep.index].props.integrations"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </rest-form-item>
        </el-col>
      </el-row>

      <el-row v-if="model.source.type === 'fs'">
        <el-col :span="6">
          <rest-form-item
            :label="$t('Imports_Field_FileDirectory')"
            :hint="$t('Imports_Field_FileDirectory_Hint')"
            prop="source.file_directory"
          >
            <el-input
              v-model="model.source.file_directory"
              :placeholder="$t('Imports_Field_FileDirectory_Placeholder')"
            />
          </rest-form-item>
        </el-col>

        <el-col :span="6">
          <rest-form-item
            :label="$t('Imports_Field_FileName')"
            :hint="$t('Imports_Field_FileName_Hint')"
            prop="source.file_name"
          >
            <el-input v-model="model.source.file_name" />
          </rest-form-item>
        </el-col>
      </el-row>
    </form-section>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import CsvUpload from '@/modules/imports/components/CsvUpload'
import EnumSelect from '@/common/components/form/EnumSelect'
import {
  sourceTypesLabels,
  selectionRulesLabels
} from '@/modules/imports/enum/ImportsEnum'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import RadioGroup from '@/common/components/form/RadioGroup'
import Radio from '@/common/components/form/Radio'
import RadioCustom from '@/modules/campaign-manage/components/locations/RadioCustom'

export default {
  name: 'StepImportDataSource',

  components: {
    RadioCustom,
    Radio,
    RadioGroup,
    FormSection,
    RestFormItem,
    CsvUpload,
    EnumSelect
  },

  props: {
    model: {
      type: Object,
      required: true
    },

    steps: {
      type: Array,
      required: true
    },

    currentStep: {
      type: Object,
      required: true
    }
  },

  mounted() {
    this.$store.dispatch('reloadProjectConfig')
    this.fetchEvents({ sort: 'title' })
  },

  data() {
    return {
      sourceTypesLabels,
      selectionRulesLabels,
      loading: false,
      events: [],
      search: ''
    }
  },

  watch: {
    type() {
      this.init()
    },
    'model.type': {
      handler(identity) {
        if (identity !== 'event') {
          this.model.config.event = ''
        }
      }
    },

    'model.config.columns': {
      deep: true,
      handler() {
        this.updateCols()
      }
    }
  },

  computed: {
    schemaEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `imports/preview`
      })
    },

    disableButton() {
      return !this.model.source.integration_id || !this.model.source.file_name
    },

    type() {
      return this.model.source.type
    },

    sourceEnum() {
      if (
        this.steps[this.currentStep.index].props.hasOwnProperty('sources') &&
        Object.keys(this.steps[this.currentStep.index].props.sources).length > 0
      ) {
        return this.steps[this.currentStep.index].props.sources
      } else {
        return this.sourceTypesLabels
      }
    },

    eventsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'events'
      })
    }
  },

  methods: {
    fetchEvents(params) {
      RestApi({
        ...this.eventsEndpoint,
        params: params
      })
        .then(({ data }) => {
          this.events = data
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    fetchEventsOnSearch(query) {
      this.search = query
      this.fetchEvents({ title: query })
    },

    init() {
      this.model.config.columns = []
    },

    getSchema() {
      this.init()
      this.loading = true
      RestApi({
        ...this.schemaEndpoint,
        ...{
          data: {
            type: this.model.source.type,
            integration_id: this.model.source.integration_id,
            file_directory: this.model.source.file_directory,
            file_name: this.model.source.file_name
          }
        }
      })
        .then(({ data }) => {
          this.model.config.columns = data.columns
          this.updateCols()
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    updateCols() {
      const { columns } = this.model.config

      columns.forEach(col => {
        const attribute = this.getMatchingAttribute(col.name)
        if (attribute && !this.isEdit) {
          this.$set(col, 'attribute', attribute)
        } else if (
          this.model.type === 'events' &&
          this.model.config.event === 'purchase'
        ) {
          this.$set(col, 'attribute', col.name)
        }
      })
    },

    getMatchingAttribute(columnName) {
      const attrs = this.steps[1].props.attributes
      const filterable = this.steps[1].props.filterableAttributes
      const grouped = this.steps[1].props.groupedFilterableAttributes
      const upperCaseName = columnName.toUpperCase()

      if (attrs.some(a => a.name.toUpperCase() === upperCaseName)) {
        return attrs.find(a => a.name.toUpperCase() === upperCaseName).name
      }

      const upperCaseFilterable = filterable.map(f => f.toUpperCase())

      if (upperCaseFilterable.includes(upperCaseName)) {
        return columnName
      }

      const allGroupAttributes = grouped.flatMap(g =>
        g.attributes.map(a => a.toUpperCase())
      )

      if (allGroupAttributes.includes(upperCaseName)) {
        return columnName
      }
    }
  }
}
</script>

<style scoped>
.custom-event {
  line-height: 3em;
}
</style>



// WEBPACK FOOTER //
// src/modules/imports/components/StepImportDataSource.vue